<template>

  <report-panel icon="$reservation" :title="title" :loading="loading">

    <div ref="chartContainer">
      <pie
        :chart-options="chartOptions"
        :chart-data="chartData"
        :styles="chartStyles"
      />
    </div>

  </report-panel>

</template>

<script setup lang="ts">

import {computed, useTemplateRef} from 'vue'
import { useI18n } from 'vue-i18n'
import ReportPanel from './ReportPanel.vue'
import { percentageFormatter } from '../../../../utils/formatters'
import useFitChartToContainer from '../../../../utils/useFitChartToContainer'

// ChartJS
import { Pie } from 'vue-chartjs'
import {
  Chart as ChartJS,
  Tooltip,
  Legend,
  ArcElement, type ChartOptions, type ChartData, type ChartDataset
} from 'chart.js'
import type { ReportingDateSummaries } from '../../../../services/api-clients/ReportingApi'
import type { BreakdownMode } from '../types'

ChartJS.register(Tooltip, Legend, ArcElement)

interface Props {
  dateSummaries: ReportingDateSummaries
  mode: BreakdownMode
  loading: boolean
}

const props = withDefaults(defineProps<Props>(), {
  loading: false,
})

const { t } = useI18n()

const title = computed(() => {
  switch (props?.mode?.name) {
    case 'channel':
      return t('titles.transactions_by_channel')
      break
    case 'vertical':
      return t('titles.transactions_by_vertical')
      break
    default:
      return t('misc.unknown_item')
      break
  }
})

/**
 * Chart
 */

const chartOptions: ChartOptions<'pie'> = {
  plugins: {
    tooltip: {
      callbacks: {
        label: (context) => {
          if (context.datasetIndex !== 0) {
            return ''
          }
          const percentage = percentageFormatter.format(context.parsed / 100)
          const absolute = context.chart.data.datasets[1].data[context.dataIndex]
          return `${context.label}: ${percentage} (${absolute})`
        },
      },
    },
  },
}

const chartData = computed<ChartData<'pie'>>(() => {

  const labels: string[] = []
  const colors: string[] = []
  const percentages: number[] = []
  const absolutes: number[] = []

  if (props.dateSummaries) {

    const labelsEnum = props.mode.labelsEnum
    const colorsEnum = props.mode.colorsEnum

    const collatedData: {total: number, by_category: {[key: string]: number}} = {
      total: 0,
      by_category: {},
    }

    // build collatedData
    Object.values(props.dateSummaries).forEach((summary) => {
      const source = summary.as_transaction_date.transactions[props.mode.dataKey]
      Object.entries(source).forEach(([category, categoryTransactions]) => {
        collatedData.total += categoryTransactions
        if (!(category in collatedData.by_category)) {
          collatedData.by_category[category] = 0
        }
        collatedData.by_category[category] += categoryTransactions
      })
    })

    Object.entries(collatedData.by_category).forEach(([category, categoryTotal]) => {
      labels.push(labelsEnum[category] ?? category)
      colors.push(colorsEnum[category] ?? '#787878')
      absolutes.push(categoryTotal)
      percentages.push(categoryTotal / collatedData.total * 100) // TODO: round?
    })

  }

  const percentageDataset: ChartDataset<'pie'> = {
    backgroundColor: colors,
    data: percentages,
  }

  const absoluteDataset: ChartDataset<'pie'> = {
    backgroundColor: colors,
    data: absolutes,
    hidden: true,
  }

  return {
    labels: labels,
    datasets: [percentageDataset, absoluteDataset],
  }

})

/**
 * Adjust the chart size responsively
 * https://vue-chartjs.org/guide/#chart-with-dynamic-styles
 */

const chartContainer = useTemplateRef('chartContainer')
const { chartStyles } = useFitChartToContainer(chartContainer)

</script>
